import FeatherIcon from 'feather-icons-react';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { Alert, Button, Modal } from 'react-bootstrap';
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
} from 'reactstrap';
import * as Yup from 'yup';
import SectionTitle from '../../components/Shared/SectionTitle';
import { useCouponCode } from '../../hooks/coupon-code';

export default function CouponCodePage() {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [errorMsg, setErrorMsg] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [discountPercent, setDiscountPercent] = useState(0);
  const [userRewardPercent, setUserRewardPercent] = useState(0);
  const [isCopied, setIsCopied] = useState(false);

  const { status, error, response, createCoupon } = useCouponCode();

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      name: '',
      phone: '',
      coupon_code: '',
      discount_percent: '',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Овог нэрээ оруулна уу'),
      phone: Yup.string()
        .required('Утасны дугаараа оруулна уу')
        .min(8, 'Утасны дугаар хамгийн багадаа 8 оронтой байх ёстой'),
      coupon_code: Yup.string().required('Купон кодоо оруулна уу'),
      discount_percent: Yup.number()
        .required('Хямдралын хувиа оруулна уу')
        .min(1, 'Хямдралын хувь хамгийн багадаа 1% байх ёстой')
        .max(20, 'Хямдралын хувь хамгийн ихдээ 20% байх ёстой')
        .integer('Хямдралын хувь бүхэл тоо байх ёстой'),
    }),
    onSubmit: (values) => {
      createCoupon(values);
    },
  });

  const handleCopyCouponCode = async () => {
    try {
      await navigator.clipboard.writeText(couponCode);
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
      const textArea = document.createElement('textarea');
      textArea.value = couponCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    }
  };

  useEffect(() => {
    if (status === 'done') {
      if (!error) {
        if (response) {
          const discount = parseInt(validation.values.discount_percent) || 0;
          const reward = 20 - discount;

          setCouponCode(response?.coupon_code);
          setDiscountPercent(discount);
          setUserRewardPercent(reward);
          setErrorMsg(false);
          setShowSuccessModal(true);
        }
      }
    } else if (status === 'error') {
      setErrorText(response?.message || 'Купон код давхардаж байна');
      setErrorMsg(true);
      setShowSuccessModal(false);
    }
  }, [error, status, response]);

  useEffect(() => {
    document.body.classList = '';

    const scrollNavigation = () => {
      var doc = document.documentElement;
      var top = (window.scrollY || doc.scrollTop) - (doc.clientTop || 0);
      if (top > 40) {
        document.getElementById('topnav').classList.add('nav-sticky');
      } else {
        document.getElementById('topnav').classList.remove('nav-sticky');
      }
    };

    window.addEventListener('scroll', scrollNavigation, true);

    return () => {
      window.removeEventListener('scroll', scrollNavigation, true);
    };
  }, []);

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    setIsCopied(false);
    validation.resetForm();
  };

  return (
    <Container className="mt-100 mt-60">
      <SectionTitle title="Zochil Agent" />

      <Row className="align-items-center justify-content-center">
        <Col lg="7" md="6" xs="12" className="mt-4 pt-2">
          <Card className="rounded border-0 shadow">
            <div className="bg-light p-4 text-center rounded-top">
              <h4 className="mb-0 text-primary">Купон код үүсгэх</h4>
            </div>

            <CardBody>
              {errorMsg && errorText && (
                <Alert
                  variant="danger"
                  onClose={() => {
                    setErrorMsg(false);
                  }}
                  dismissible
                >
                  {errorText}
                </Alert>
              )}
              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                  validation.handleSubmit();
                  return false;
                }}
              >
                <Row>
                  <Col md="12">
                    <div className="mb-3">
                      <Label className="form-label">Овог нэр:</Label>
                      <Input
                        name="name"
                        id="name"
                        type="text"
                        className="form-control fw-bold"
                        placeholder="Болд Батбилэг"
                        disabled={status === 'pending'}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.name || ''}
                        invalid={
                          validation.touched.name && validation.errors.name
                            ? true
                            : false
                        }
                      />
                      {validation.touched.name && validation.errors.name ? (
                        <FormFeedback type="invalid">
                          {validation.errors.name}
                        </FormFeedback>
                      ) : null}
                    </div>
                  </Col>
                  <Col md="12">
                    <div className="mb-3">
                      <Label className="form-label">Утасны дугаар:</Label>
                      <Input
                        type="text"
                        autoComplete="off"
                        id="phone"
                        className="form-control fw-bold"
                        placeholder="99000000"
                        name="phone"
                        disabled={status === 'pending'}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.phone || ''}
                        invalid={
                          validation.touched.phone && validation.errors.phone
                            ? true
                            : false
                        }
                      />
                      {validation.touched.phone && validation.errors.phone ? (
                        <FormFeedback type="invalid">
                          {validation.errors.phone}
                        </FormFeedback>
                      ) : null}
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="mb-3">
                      <Label className="form-label">Купон код:</Label>
                      <Input
                        type="text"
                        autoComplete="off"
                        id="coupon_code"
                        name="coupon_code"
                        disabled={status === 'pending'}
                        className="form-control fw-bold"
                        placeholder="ZOCHIL123"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.coupon_code || ''}
                        invalid={
                          validation.touched.coupon_code &&
                          validation.errors.coupon_code
                            ? true
                            : false
                        }
                      />
                      {validation.touched.coupon_code &&
                      validation.errors.coupon_code ? (
                        <FormFeedback type="invalid">
                          {validation.errors.coupon_code}
                        </FormFeedback>
                      ) : null}
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="mb-3">
                      <Label className="form-label">
                        Хямдралын хувь (1-20%):
                      </Label>
                      <Input
                        type="number"
                        min="1"
                        max="20"
                        autoComplete="off"
                        id="discount_percent"
                        name="discount_percent"
                        disabled={status === 'pending'}
                        className="form-control fw-bold"
                        placeholder="15"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.discount_percent || ''}
                        invalid={
                          validation.touched.discount_percent &&
                          validation.errors.discount_percent
                            ? true
                            : false
                        }
                      />
                      {validation.touched.discount_percent &&
                      validation.errors.discount_percent ? (
                        <FormFeedback type="invalid">
                          {validation.errors.discount_percent}
                        </FormFeedback>
                      ) : null}
                    </div>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col sm="12">
                    <div className="p-3 bg-light rounded border">
                      <h6 className="text-primary mb-2">📋 Мэдээлэл:</h6>
                      <div className="small text-muted">
                        <div className="mb-1">
                          • Хэрэглэгч купон ашиглах бүрд танд урамшуулал
                          олгогдоно.
                        </div>
                        <div>
                          • Урамшуулал = 20% - Таны тохируулсан хямдралын хувь
                          (Жишээ нь: 20% - 5% хямдралын хувь = 15% урамшуулал)
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col sm="12">
                    <Button
                      variant="primary"
                      type="submit"
                      name="send"
                      disabled={status === 'pending'}
                    >
                      {status === 'pending' && (
                        <span
                          className="spinner-border spinner-border-sm "
                          style={{ marginRight: '0.5rem' }}
                        />
                      )}
                      Үүсгэх
                    </Button>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Modal show={showSuccessModal} onHide={handleCloseSuccessModal} centered>
        <Modal.Header closeButton className="bg-success text-white">
          <Modal.Title>
            <i className="fas fa-check-circle me-2"></i>
            Амжилттай үүслээ!
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center mb-3">
            <div className="bg-light p-3 rounded d-flex align-items-center position-relative">
              <h5 className="mb-0 text-primary" style={{ marginRight: '1rem' }}>
                Купон код: <strong>{couponCode}</strong>
              </h5>
              <Button
                variant={isCopied ? 'success' : 'outline-primary'}
                size="sm"
                onClick={handleCopyCouponCode}
                className="p-2"
              >
                {isCopied ? (
                  <>
                    <FeatherIcon icon="check" className="fea icon-sm icons" />
                  </>
                ) : (
                  <FeatherIcon icon="copy" className="fea icon-sm icons" />
                )}
              </Button>
            </div>
          </div>

          <hr className="my-3" />

          <div>
            <h6 className="mb-3">
              <strong>📋 Купон кодын мэдээлэл:</strong>
            </h6>
            <div className="mb-2">
              • Хэрэглэгчид <strong>{discountPercent}%</strong> хямдрал
              олгогдоно.
            </div>
            <div className="mb-2">
              • Купон ашигласан үед танд <strong>{userRewardPercent}%</strong>{' '}
              урамшуулал олгогдоно.
            </div>
            <div className="mt-3 p-2 bg-light rounded">
              <small className="">
                <em>
                  💡 Тооцоо: 20% - {discountPercent}% хямдралын хувь ={' '}
                  {userRewardPercent}% урамшуулал
                </em>
              </small>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleCloseSuccessModal}>
            Хаах
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
